import { useEffect } from 'react';
import { Routes, Route, useLocation, Outlet, Navigate } from 'react-router-dom';

/** Keep scroll position predictable on route change (incl. mobile Back/Forward). */
function ScrollToTop() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' as ScrollBehavior : 'auto' });
    }, [pathname, search]);
    return null;
}
import Home from './pages/Home.tsx';
import ProductDetail from './pages/ProductDetail.tsx';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import ConfirmEmail from './pages/ConfirmEmail.tsx';
import CheckEmail from './pages/CheckEmail.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import SellProduct from './pages/ProductForm.tsx';
import MyAccount from './pages/MyAccount.tsx';
import AccountCentre from './pages/AccountCentre.tsx';
import AccountOrdersSection from './components/account/AccountOrdersSection.tsx';
import AccountSalesSection from './components/account/AccountSalesSection.tsx';
import AccountProductsSection from './components/account/AccountProductsSection.tsx';
// FUTURE: chat — re-enable route when chat ships
// import Chat from './pages/Chat.tsx';
import AdminLogin from './admin/pages/AdminLogin.tsx';
import AdminDashboard from './admin/pages/AdminDashboard.tsx';
import ShopProtectedRoute from './components/ShopProtectedRoute.tsx';
import AdminProtectedRoute from './components/AdminProtectedRoute.tsx';
// Public admin register removed (privilege escalation). Admins: seed (dev) or Users admin API.
// import AdminRegister from './admin/pages/AdminRegister.tsx';
import { SiteSettings } from './types/index.ts';
import AccountStatus from './pages/AccountStatus.tsx';
import ShopLayout from './components/ShopLayout.tsx';
import SearchResults from './pages/SearchResults.tsx';
import Checkout from './pages/Checkout.tsx';
import MyRebaby from './pages/MyRebaby.tsx';
import Wishlist from './pages/Wishlist.tsx';
import Helpdesk from './pages/Helpdesk.tsx';
import OrderIssueForm from './pages/OrderIssueForm.tsx';
import UserProfile from './pages/UserProfile.tsx';
import CommunityRules from './pages/CommunityRules.tsx';
import PrivacyPolicy from './pages/PrivacyPolicy.tsx';
import TermsOfService from './pages/TermsOfService.tsx';
import About from './pages/About.tsx';
import FAQ from './pages/FAQ.tsx';
import HowItWorks from './pages/HowItWorks.tsx';
import Shipping from './pages/Shipping.tsx';
import Partner from './pages/Partner.tsx';
import Blog from './pages/Blog.tsx';
import OrderDetails from './components/OrderDetails.tsx';
import { getSiteSettings } from './services/api.shop';

function App() {
    const location = useLocation();

    // Admin shell: never index in Google
    useEffect(() => {
        if (!location.pathname.toLowerCase().includes('/admin')) return;
        let el = document.head.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
        if (!el) {
            el = document.createElement('meta');
            el.name = 'robots';
            document.head.appendChild(el);
        }
        el.content = 'noindex, nofollow';
        return () => {
            el!.content = 'index, follow';
        };
    }, [location.pathname]);

    // === YOUR EXISTING THEME LOGIC (unchanged) ===
    useEffect(() => {
        const isAdminRoute = location.pathname.toLowerCase().includes('admin');
        if (isAdminRoute) {
            // Neutral admin shell — never apply SiteSettings shop colors (My Journey designer, etc.)
            document.documentElement.setAttribute('data-app', 'admin');
            document.documentElement.style.setProperty('--bg-image', 'none');
            document.documentElement.style.setProperty('--bg-color', '#f3f4f6');
            document.documentElement.style.setProperty('--text-color', '#1f2937');
            document.documentElement.style.setProperty('--accent-color', '#3b82f6');
            document.documentElement.style.setProperty('--highlight-color', '#4f46e5');
            return;
        }

        document.documentElement.setAttribute('data-app', 'shop');

        // Must match index.css :root + index.html early-paint + SiteSettings defaults (Figma)
        const fallback: SiteSettings = {
            id: 0,
            textColor: '#3D3830',
            backgroundColor: '#F7F3EB',
            accentColor: '#C9A86C',
            highlightColor: '#A67C52',
            backgroundImageUrl: '',
            contactEmail: 'support@petito.be',
            facebookUrl: '',
            instagramUrl: '',
            tikTokUrl: '',
            pinterestUrl: '',
        };

        // v2: invalidate old green/white caches that caused FOUC after Petito Figma palette
        const THEME_KEY = 'siteTheme_v2';
        try {
            localStorage.removeItem('siteTheme');
        } catch {
            /* ignore */
        }

        const cached = localStorage.getItem(THEME_KEY);
        let settings: SiteSettings = fallback;
        let hadCache = false;

        if (cached) {
            try {
                settings = JSON.parse(cached) as SiteSettings;
                hadCache = true;
            } catch (e) {
                console.error('Invalid cached theme:', e);
                localStorage.removeItem(THEME_KEY);
            }
        }

        applyTheme(settings);

        // Always GET + applyTheme: server is source of truth; localStorage only avoids first-paint flash
        const fetchLatest = async () => {
            try {
                const latest = (await getSiteSettings()) || fallback;
                applyTheme(latest);
                localStorage.setItem(THEME_KEY, JSON.stringify(latest));
            } catch (err) {
                console.error('Failed to load site settings:', err);
                // Keep last good localStorage theme if fetch fails (already applied above when present)
                if (!hadCache) applyTheme(fallback);
            }
        };

        fetchLatest();

        // Help surface admin theme changes without hard refresh for visitors
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchLatest();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [location.pathname]);

    const applyTheme = (settings: SiteSettings) => {
        document.documentElement.style.setProperty('--text-color', settings.textColor);
        document.documentElement.style.setProperty('--accent-color', settings.accentColor);
        document.documentElement.style.setProperty(
            '--highlight-color',
            settings.highlightColor?.trim() || settings.textColor || '#A67C52'
        );

        if (settings.backgroundImageUrl && settings.backgroundImageUrl.trim()) {
            document.documentElement.style.setProperty('--bg-image', `url(${settings.backgroundImageUrl})`);
            document.documentElement.style.setProperty('--bg-color', 'transparent');
        } else {
            document.documentElement.style.setProperty('--bg-image', 'none');
            document.documentElement.style.setProperty('--bg-color', settings.backgroundColor);
        }
        // Enable soft transitions only after first paint (avoids FOUC animation)
        document.documentElement.classList.add('theme-ready');
    };

    // Show loading screen while translations are loading
    //if (isLoading) {
    //    return (
    //        <div className="min-h-screen flex items-center justify-center bg-[#FAF5DC]">
    //            <div className="text-center">
    //                <div className="w-12 h-12 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
    //                <p className="text-[#2E7D32] text-lg">Loading translations...</p>
    //            </div>
    //        </div>
    //    );
    //}

    return (
        <div className="min-h-screen">
            <ScrollToTop />
            <Routes>
                {/* ========== WEBSHOP / FRONTEND ROUTES ========== */}
                <Route element={<Outlet />}>
                    <Route element={<ShopLayout />}>
                        <Route path="/" element={<Navigate to="/home" replace />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/products/:id" element={<ProductDetail />} />
                        <Route path="/search" element={<SearchResults />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/account-status" element={<AccountStatus />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/check-email" element={<CheckEmail />} />
                        <Route path="/confirm-email" element={<ConfirmEmail />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        {/* Protected shop routes */}
                        <Route path="/checkout/:productId" element={<ShopProtectedRoute><Checkout /></ShopProtectedRoute>} />
                        <Route path="/sell" element={<ShopProtectedRoute><SellProduct /></ShopProtectedRoute>} />
                        <Route path="/edit-product/:id" element={<ShopProtectedRoute><SellProduct /></ShopProtectedRoute>} />
                        <Route path="/my-account" element={<ShopProtectedRoute><MyAccount /></ShopProtectedRoute>} />
                        <Route path="/my-account/orders" element={<ShopProtectedRoute><AccountOrdersSection /></ShopProtectedRoute>} />
                        <Route path="/my-account/sales" element={<ShopProtectedRoute><AccountSalesSection /></ShopProtectedRoute>} />
                        {/* Short aliases — /sales alone 404'd; keep bookmarks working */}
                        <Route path="/sales" element={<ShopProtectedRoute><Navigate to="/my-account/sales" replace /></ShopProtectedRoute>} />
                        <Route path="/orders" element={<ShopProtectedRoute><Navigate to="/my-account/orders" replace /></ShopProtectedRoute>} />
                        <Route path="/my-account/listings" element={<ShopProtectedRoute><AccountProductsSection /></ShopProtectedRoute>} />
                        <Route path="/account-centre" element={<ShopProtectedRoute><AccountCentre /></ShopProtectedRoute>} />
                        <Route path="/orders/:id" element={<ShopProtectedRoute><OrderDetails /></ShopProtectedRoute>} />
                        <Route path="/my-petito" element={<ShopProtectedRoute><MyRebaby /></ShopProtectedRoute>} />
                        {/* Brand rename: keep old path working */}
                        <Route path="/my-rebaby" element={<Navigate to="/my-petito" replace />} />
                        <Route path="/favourites" element={<ShopProtectedRoute><Wishlist /></ShopProtectedRoute>} />
                        {/* FUTURE: chat
                        <Route path="/chat/:productId/:sellerId" element={<ShopProtectedRoute><Chat /></ShopProtectedRoute>} />
                        */}
                        <Route path="/helpdesk" element={<ShopProtectedRoute><Helpdesk /></ShopProtectedRoute>} />
                        <Route path="/helpdesk/order-issue" element={<ShopProtectedRoute><OrderIssueForm /></ShopProtectedRoute>} />
                        <Route path="/users/:userId" element={<UserProfile />} />
                        {/* Public pages */}
                        <Route path="/rules" element={<CommunityRules />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/shipping" element={<Shipping />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/partner" element={<Partner />} />
                        <Route path="/blog" element={<Blog />} />
                    </Route>
                </Route>
                {/* ========== ADMIN ROUTES (completely separate) ========== */}
                <Route element={<Outlet />}>
                    <Route path="/admin/login" element={<AdminLogin />} />
                    {/* Public admin register removed — no open /admin/register */}
                    <Route path="/admin/*" element={<AdminProtectedRoute requireAdmin><AdminDashboard /></AdminProtectedRoute>} />
                </Route>

                <Route path="*" element={<div>404 Not Found</div>} />
            </Routes>
        </div>
    );
}

export default App;